import { useEffect, useMemo, useState } from "react";
import { useStore, type PanelId } from "./state/store";
import { validateProject } from "./model/validation";
import { SettingsPanel } from "./panels/SettingsPanel";
import { GuidePanel } from "./panels/GuidePanel";
import { ItemsPanel } from "./panels/ItemsPanel";
import { RecipesPanel } from "./panels/RecipesPanel";
import { LanguagesPanel } from "./panels/LanguagesPanel";
import { ImportPanel } from "./panels/ImportPanel";
import { ExportPanel } from "./panels/ExportPanel";

const NAV: { id: PanelId; label: string; icon: string }[] = [
  { id: "settings", label: "Settings", icon: "⚙" },
  { id: "guide", label: "Guidebook", icon: "📖" },
  { id: "items", label: "Items", icon: "🧱" },
  { id: "recipes", label: "Recipes", icon: "🔨" },
  { id: "languages", label: "Languages", icon: "🌐" },
  { id: "import", label: "Import", icon: "📥" },
  { id: "export", label: "Export", icon: "📦" },
];

const PANEL_LABEL: Record<PanelId, string> = Object.fromEntries(NAV.map((n) => [n.id, n.label])) as Record<
  PanelId,
  string
>;

export function App() {
  const panel = useStore((s) => s.ui.panel);
  const setPanel = useStore((s) => s.setPanel);
  const project = useStore((s) => s.project);

  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile drawer whenever the panel changes.
  useEffect(() => setNavOpen(false), [panel]);

  const issues = useMemo(() => validateProject(project), [project]);
  const errorCount = issues.filter((i) => i.severity === "error").length;
  const warningCount = issues.filter((i) => i.severity === "warning").length;

  return (
    <div className="app">
      <header className="app-header">
        <button
          className="nav-toggle"
          aria-label={navOpen ? "Close menu" : "Open menu"}
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          {navOpen ? "✕" : "☰"}
        </button>
        <div className="brand">
          <span className="brand-mark">⛏</span>
          <div>
            <div className="brand-title">Guidebook Generator</div>
            <div className="brand-sub">
              {project.meta.name} · <code>{project.namespace}</code>
            </div>
          </div>
        </div>
        <div className="header-status">
          {errorCount > 0 ? (
            <span className="status-pill error">{errorCount} error{errorCount === 1 ? "" : "s"}</span>
          ) : (
            <span className="status-pill ok">Valid</span>
          )}
          {warningCount > 0 && <span className="status-pill warning">{warningCount} warning{warningCount === 1 ? "" : "s"}</span>}
        </div>
      </header>

      <div className="app-body">
        <nav className={`sidebar ${navOpen ? "open" : ""}`}>
          {NAV.map((n) => (
            <button key={n.id} className={`nav-item ${panel === n.id ? "active" : ""}`} onClick={() => setPanel(n.id)}>
              <span className="nav-icon">{n.icon}</span>
              <span>{n.label}</span>
            </button>
          ))}
        </nav>
        {navOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} />}

        <main className="content">
          {/* Mobile-only breadcrumb showing the current panel next to the menu button. */}
          <div className="mobile-panel-bar">{PANEL_LABEL[panel]}</div>
          {panel === "settings" && <SettingsPanel />}
          {panel === "guide" && <GuidePanel />}
          {panel === "items" && <ItemsPanel />}
          {panel === "recipes" && <RecipesPanel />}
          {panel === "languages" && <LanguagesPanel />}
          {panel === "import" && <ImportPanel />}
          {panel === "export" && <ExportPanel />}
        </main>
      </div>
    </div>
  );
}
